import type { Action, ListenerEffectAPI, ThunkAction } from '@reduxjs/toolkit';
import { addListener, type createListenerMiddleware, type ThunkDispatch, type UnknownAction } from '@reduxjs/toolkit';
import type { customerUserSlice } from '@src/components/DetailsDrawer/Customer/CustomerUserDetailsDrawer/state/slice';
import type { ManualRFQPricingState } from '@src/components/OMS/ManualRFQPricing/types';
import type { PreTradeState } from '@src/components/PreTrade/redux/preTradeSlice';
import type { PortfolioViewLayoutState } from '@src/containers/Portfolio/PortfolioManagement/stateManagement/portfolioViewLayoutSlice';
import type { filterSliceReducer } from '@src/containers/Trading/Markets/Options/Filter/filterSlice';
import type { AppConfigReduxState } from '@src/providers/AppConfigRedux/AppConfigSlice';
import type { appNotificationsStreamingApi } from '@src/providers/AppNotifications/redux/AppNotificationsApi';
import type { AppNotificationsState } from '@src/providers/AppNotifications/redux/AppNotificationsSlice';
import type { CoinMetricsCredentialsState } from '@src/providers/CoinMetricsApiProvider/coinMetricsCredentialsSlice';
import type { IWebSocketClient } from '@talos/kyoko';
import type { AppLayoutState } from '../../components/AppLayout/AppLayoutSlice';
import type { MultilegComboState } from '../../components/MultilegCombo/types';
import type { ActiveOrderCardsState } from '../../components/OMS/Cards/types';
import type { CareOrderState } from '../../components/OMS/CareOrderFormView/types';
import type { DetailsDrawerState } from '../../components/OMS/Details/DetailsDrawerSlice';
import type { ManualDealTradeState } from '../../components/OMS/ManualDealTradeView/types';
import type { ManualOrderFillState } from '../../components/OMS/ManualOrderFillView/ManualOrderFillSlice';
import type { ManualTradeEntryState } from '../../components/OMS/ManualTradeView/ManualTradeSlice';
import type { OrderState } from '../../components/OMS/NewOrder/types';
import type { RFQState } from '../../components/OMS/NewRFQ/types';
import type { OMSRealTimeSettingsState } from '../../components/OMS/OMSRealTimeSettingsSlice';
import type { OrderPresetState } from '../../components/OMS/Presets/types';
import type { SalesOrderState } from '../../components/OMS/SalesOrder/types';
import type { SalesRFQState } from '../../components/OMS/SalesRFQView/types';
import type { OMSReferenceDataState, OMSState } from '../../components/OMS/types';
import type { baseRestApi } from '../../utils/rtkQuery/baseRestApi';
import type { KeyedState } from './keyedReducer';
import type { navigationSliceReducer } from './navigationSlice';
import type { streamingDataSlice } from './streamingDataSlice';
import type { createUserInfoSlice } from './userInfoSlice';

export type AppState = {
  appConfig: AppConfigReduxState;
  coinMetricsCredentials: CoinMetricsCredentialsState;
  appLayout: AppLayoutState;
  appNotifications: AppNotificationsState;
  appNotificationsStreamingApi: ReturnType<typeof appNotificationsStreamingApi.reducer>;
  baseRestApi: ReturnType<typeof baseRestApi.reducer>;
  cards: ActiveOrderCardsState;
  careOrder: CareOrderState;
  customerUserDrawer: ReturnType<typeof customerUserSlice>;
  detailsDrawer: DetailsDrawerState;
  manualDeal: ManualDealTradeState;
  manualOrderFill: ManualOrderFillState;
  manualRFQPricing: KeyedState<ManualRFQPricingState>;
  manualTrade: ManualTradeEntryState;
  multilegCombo: MultilegComboState;
  navigation: ReturnType<typeof navigationSliceReducer>;
  OMS: OMSState;
  optionsTab: ReturnType<typeof filterSliceReducer>;
  order: OrderState;
  portfolioViewLayout: PortfolioViewLayoutState;
  preTrade: PreTradeState;
  presets: OrderPresetState;
  realTimeSettings: OMSRealTimeSettingsState;
  referenceData: OMSReferenceDataState;
  rfq: RFQState;
  salesOrder: SalesOrderState;
  salesRFQ: SalesRFQState;
  streamingData: ReturnType<typeof streamingDataSlice.reducer>;
  userInfo: ReturnType<ReturnType<typeof createUserInfoSlice>['reducer']>;
};

export interface AppExtraArgument {
  wsClient: IWebSocketClient<unknown>;
}

export type AppThunkDispatch = ThunkDispatch<AppState, AppExtraArgument, UnknownAction>;
export type AppListenerEffectAPI = ListenerEffectAPI<AppState, AppThunkDispatch, AppExtraArgument>;

export type AppStateListenerStart = ReturnType<typeof buildAppStateListener>;
export const buildAppStateListener = (listenerMiddleware: ReturnType<typeof createListenerMiddleware>) => {
  return listenerMiddleware.startListening.withTypes<AppState, AppThunkDispatch, AppExtraArgument>();
};

export const addAppListener = addListener.withTypes<AppState, AppThunkDispatch>();

export type AppStateThunkAction = ThunkAction<void, AppState, unknown, Action<string>>;
